/**
 * The invoices behind the rows that are waiting on an answer.
 *
 * The list endpoint sends a stage but not the request that put an enquiry at
 * it, and the tick in a row's Status cell needs the request itself — its id to
 * settle, its amount and plan to ask about. Reading it when the tick is
 * pressed puts a wait between the press and the question, which is a wait over
 * something the screen could have known already.
 *
 * So it is read as the page lands instead: one enquiry per row standing at
 * "Payment sent", which is a handful at most on a page of ten, and none at all
 * for the ordinary list where nothing is outstanding.
 *
 * What comes back goes into the enquiries store rather than into state here, so
 * the rows pick it up the same way they pick up an answer given in this sitting
 * — see `useEnquiries`. Nothing is said about a read that fails: this is
 * groundwork for a press that may never come, and the press does its own read
 * and reports for itself.
 */

import { useEffect, useRef } from "react";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import {
  fetchEnquiry,
  noteEnquiryPayment,
} from "../services/enquiriesService";
import type { Enquiry } from "../types/enquiries.types";

export function useEnquiryPaymentRequests(
  enquiries: Enquiry[],
  /**
   * Off where the two answers are — a workspace with no plan behind it cannot
   * settle anything, so reading what it would settle is a call made for a press
   * that is going to be turned back. See `useSubscriptionGate`.
   */
  enabled = true
): void {
  const getToken = useTokenGetter();

  // Every enquiry this mounting has already gone after, so a row that is
  // re-rendered — and they all are, each time one of these lands — is not
  // fetched again. Once per row, whatever came of it: a read that failed is
  // left to the press, which has its own and can say what happened.
  const attempted = useRef(new Set<string>());

  useEffect(() => {
    if (!enabled) return;

    enquiries.forEach((enquiry) => {
      if (enquiry.payment || enquiry.status !== "Payment sent") return;
      if (attempted.current.has(enquiry.id)) return;
      attempted.current.add(enquiry.id);

      fetchEnquiry(getToken, enquiry.id)
        .then((record) => {
          // Not guarded on the component still being here: this writes to the
          // store rather than to state, and an answer that arrives after the
          // screen has gone is worth keeping for the next one that opens.
          if (record.payment) noteEnquiryPayment(enquiry.id, record.payment);
        })
        .catch((reason) =>
          console.error("enquiry payment request read failed:", reason)
        );
    });
  }, [enquiries, enabled, getToken]);
}
